import yup from './yup'
import moment from 'moment/moment'

export const confFormSchema = yup.object().shape({
  confName: yup.string().label('Conference Name').trimmedRequired('Conference Name is required.'),
  confCode: yup.string().label('Conference Code').trimmedRequired('Conference Code is required'),
  timezone: yup.string().label('Conference Timezone').required('Conference Timezone is required'),
  startDate: yup.date().label('Start Date').required().typeError('Invalid date'),
  endDate: yup
    .date()
    .label('End date')
    .typeError('Invalid date')
    .test('Date Check', 'End date must be same or after start date.', function (value) {
      const startDate = moment(this.parent.startDate)
      const endDate = moment(value)
      return endDate.isSameOrAfter(startDate, 'date')
    })
    .required(),
  viewableUntil: yup
    .date()
    .label('Viewable Until Date')
    .typeError('Invalid date')
    .test('Viewable Until date must be same or after end date.', function (value) {
      const endDate = moment(this.parent.endDate)
      const viewableDate = moment(value)
      return viewableDate.isSameOrAfter(endDate, 'date')
    })
    .required(),
  is24hrFormat: yup.bool().label('Use 24hr Format').required(),
  isActive: yup.bool().label('Status').required(),
  posterPrefix: yup
    .string()
    .min(2)
    .max(3)
    .matches(/^[a-zA-Z]+$/, 'Poster Number Prefix can only contain letters.')
    .label('Poster Session Prefix')
    .optional(),
  abstractPrefix: yup
    .string()
    .min(2)
    .max(3)
    .matches(/^[a-zA-Z]+$/, 'Abstract Number Prefix can only contain letters.')
    .label('Abstract Prefix')
    .optional(),
  havePolls: yup.boolean().label('Have Polls'),
  badgeQrCodeFormat: yup
    .string()
    .oneOf(['plain', 'vcard'])
    .label('Badge QR Code Format')
    .optional(),
  speakerCanUploadTalks: yup.boolean().label('Speaker Can Upload Talks'),
  enableAttendance: yup.boolean().label('Enable Attendance'),
  enableResources: yup.boolean().label('Enable Resources'),
  tags: yup.array().of(
    yup.object().shape({
      id: yup.string(),
      name: yup.string().required('Tag name is required'),
      color: yup.string().required('Tag color is required')
    })
  ),
  attendanceStartTime: yup
    .string()
    .label('Start Time')
    .when('enableAttendance', {
      is: true,
      then: yup.string().required('Start Time is required'),
      otherwise: yup.string().nullable()
    }),
  attendanceEndTime: yup
    .string()
    .label('End Time')
    .when('enableAttendance', {
      is: true,
      then: yup
        .string()
        .required('End Time is required')
        .test(
          'greater than start time',
          'End Time must be greater than Start Time',
          (value, context) => {
            const startValue = context.parent.attendanceStartTime
            const enableAttendance = context.parent.enableAttendance
            return enableAttendance
              ? new Date(value || 0).getTime() > new Date(startValue).getTime()
              : true
          }
        ),
      otherwise: yup.string().nullable()
    }),
  attendanceInfoMessage: yup
    .string()
    .label('Attendance Explanation Message')
    .when('enableAttendance', {
      is: true,
      then: yup.string().required('Attendance Explanation Message is required'),
      otherwise: yup.string()
    }),
  defaultGraceTime: yup
    .number()
    .label('Grace Time')
    .when('enableAttendance', {
      is: true,
      then: yup
        .number()
        .typeError('Grace Time must be a number')
        .integer('Grace Time must be a whole number of minutes')
        .min(0, 'Grace Time cannot be negative')
        .required('Grace Time is required'),
      otherwise: yup.number().nullable()
    }),
  resourceContent: yup
    .string()
    .label('Resource Content')
    .when('enableResources', {
      is: true,
      then: yup.string().trimmedRequired('Resource Content is required'),
      otherwise: yup.string()
    }),
  organizerId: yup.string().nullable().optional(),
  users: yup
    .array()
    .of(
      yup.object().shape({
        fullName: yup.string().trimmedRequired('Full name is required'),
        email: yup.string().email('Invalid email').required('Email is required'),
        role: yup
          .string()
          .oneOf(['owner', 'admin'], 'Role must be owner or admin')
          .required('Role is required'),
        password: yup
          .string()
          .min(6, 'Password must be at least 6 characters')
          .required('Password is required')
      })
    )
    .test('unique-owner', 'Only one owner is allowed per conference', function (users) {
      if (!users || users.length === 0) return true
      const owners = users.filter((user) => user.role === 'owner')
      return owners.length <= 1
    })
    .test('unique-emails', 'Email addresses must be unique', function (users) {
      if (!users || users.length === 0) return true
      const emails = users.map((user) => user.email?.toLowerCase()).filter(Boolean)
      return new Set(emails).size === emails.length
    })
})
